/**
 * CreateComposer — THE Linear-style create modal, the *create* sibling of
 * `PeekDialog` (which is the *view* standard). Every "create X" flow (issue,
 * goal, person, org, project, lead, opportunity, …) renders THIS one chrome and
 * supplies only its own data: a borderless title + description, a row of
 * property PILLS, the matched Expand/Close ghost pair, and a footer with an
 * attachment button, a "Create more" toggle, and one primary Create button.
 *
 * DRY contract: no create flow re-rolls modal chrome. Single-select properties
 * use `ComposerSelectPill` (the `SelectTrigger` `pill` variant); searchable /
 * custom properties wrap `ComposerPill` (a matching pill button) in a Popover.
 * Reuses `PeekGlyphExpand`/`PeekGlyphClose`, `Textarea autosize`, `Switch`.
 */

import * as React from "react";
import { RouterLink as NextLink } from "../router";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../dialog";
import { Button } from "../button";
import { Textarea } from "../textarea";
import { Switch } from "../switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import { Paperclip, X } from "../icons";
import { cn } from "../utils";
import { PeekGlyphExpand, PeekGlyphClose } from "./peek-dialog";

/* ── The property pill (matches the SelectTrigger `pill` variant) ──────────── */

/**
 * A rounded property-pill trigger — the soft-filled chip look shared with
 * `SelectTrigger variant="pill"`, for pills whose picker is a Popover/Command
 * (assignee, labels) rather than a plain Select. `forwardRef` + spreads props so
 * it drops straight into `PopoverTrigger asChild`. Set `active` when a value is
 * chosen (muted → foreground).
 */
export const ComposerPill = React.forwardRef<
  HTMLButtonElement,
  {
    icon?: React.ReactNode;
    active?: boolean;
    className?: string;
    children: React.ReactNode;
  } & Omit<React.ComponentProps<"button">, "children">
>(function ComposerPill({ icon, active, className, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      data-slot="composer-pill"
      className={cn(
        "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full bg-foreground/[0.07] px-3 type-body leading-none whitespace-nowrap transition-colors outline-none hover:bg-foreground/[0.12] focus-visible:bg-foreground/[0.12] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:opacity-70",
        active ? "text-foreground" : "text-muted-foreground",
        className,
      )}
      {...props}
    >
      {icon}
      <span className="truncate">{children}</span>
    </button>
  );
});

/* ── The single-select pill (the common case) ──────────────────────────────── */

export interface ComposerPillOption {
  value: string;
  label: string;
  /** Optional leading glyph rendered both in the trigger and the item. */
  icon?: React.ReactNode;
}

/**
 * ComposerSelectPill — a property pill backed by a plain `Select`, reusing the
 * atom's `pill` trigger variant so it matches `ComposerPill` 1:1. For the common
 * single-select property (status, priority, kind, role, cadence, a short project
 * list). `placeholder` shows when unset (muted); the chosen option's icon leads.
 */
export function ComposerSelectPill({
  icon,
  placeholder,
  value,
  onValueChange,
  options,
  ariaLabel,
}: {
  /** Fallback leading glyph when the chosen option has none / nothing is set. */
  icon?: React.ReactNode;
  placeholder: string;
  value: string;
  onValueChange: (v: string) => void;
  options: ComposerPillOption[];
  ariaLabel?: string;
}) {
  const selected = options.find((o) => o.value === value);
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        variant="pill"
        size="sm"
        aria-label={ariaLabel}
        className={cn(
          "gap-1.5 [&_svg:not([data-slot=select-value])]:opacity-70",
          selected ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {selected?.icon ?? icon}
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.icon}
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/* ── The composer modal ────────────────────────────────────────────────────── */

export interface CreateComposerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /** Breadcrumb context chip (e.g. workspace / kind) rendered before "› heading". */
  contextLabel?: string;
  contextIcon?: React.ReactNode;
  /** Custom breadcrumb leading node (e.g. a type switcher), rendered INSTEAD of
   *  the `contextLabel` chip. Used by the global "+" modal to switch entity type. */
  context?: React.ReactNode;
  /** The "New {thing}" label after the chip. */
  heading: string;

  /** Full-form destination; when set the Expand control renders beside Close. */
  expandHref?: string;
  /** Fired alongside expand navigation (e.g. close the modal). */
  onExpand?: () => void;

  title: string;
  onTitleChange: (v: string) => void;
  titlePlaceholder: string;

  /** Description is optional — omit `onDescriptionChange` to hide the field. */
  description?: string;
  onDescriptionChange?: (v: string) => void;
  descriptionPlaceholder?: string;

  /** The property-pill row (ComposerSelectPill / Popover+ComposerPill nodes). */
  pills?: React.ReactNode;
  /** Extra fields between description and pills (rare — most data is a pill). */
  children?: React.ReactNode;

  /** Attachment tray — omit `onAttachmentsChange` to hide the paperclip. */
  attachments?: File[];
  onAttachmentsChange?: (files: File[]) => void;

  /** "Create more" toggle — omit `onCreateMoreChange` to hide it. */
  createMore?: boolean;
  onCreateMoreChange?: (v: boolean) => void;

  submitLabel: string;
  onSubmit: () => void;
  canSubmit: boolean;
  pending?: boolean;
  error?: string | null;

  /** Panel width override (default `sm:max-w-2xl`, matching Linear's generous width). */
  className?: string;
}

const BORDERLESS =
  "resize-none border-transparent bg-transparent px-0 shadow-none outline-none hover:border-transparent focus-visible:border-transparent placeholder:text-muted-foreground/60";

export function CreateComposer({
  open,
  onOpenChange,
  contextLabel,
  contextIcon,
  context,
  heading,
  expandHref,
  onExpand,
  title,
  onTitleChange,
  titlePlaceholder,
  description,
  onDescriptionChange,
  descriptionPlaceholder = "Add description…",
  pills,
  children,
  attachments,
  onAttachmentsChange,
  createMore,
  onCreateMoreChange,
  submitLabel,
  onSubmit,
  canSubmit,
  pending,
  error,
  className,
}: CreateComposerProps) {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const files = attachments ?? [];

  function addFiles(list: FileList | null) {
    if (!list || !onAttachmentsChange) return;
    onAttachmentsChange([...files, ...Array.from(list)]);
  }
  function removeFile(i: number) {
    onAttachmentsChange?.(files.filter((_, idx) => idx !== i));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn("gap-0 bg-background p-0", className ?? "sm:max-w-2xl")}
      >
        {/* Radix a11y names — the visible masthead is custom below. */}
        <DialogHeader className="sr-only">
          <DialogTitle>{heading}</DialogTitle>
          <DialogDescription>
            {`Create a new ${heading.replace(/^New\s+/i, "")}.`}
          </DialogDescription>
        </DialogHeader>

        {/* Masthead — context chip + heading left, Expand/Close ghost pair right. */}
        <div className="flex items-center justify-between gap-4 px-5 pt-4 pb-1">
          <div className="flex min-w-0 items-center gap-1.5 type-body">
            {context ??
              (contextLabel ? (
                // badge-ignore: masthead breadcrumb context crumb ([icon] parent label sitting before the "›" separator) — a location indicator, not a categorical status tag
                <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/60 px-1.5 py-0.5 type-subtext font-medium text-foreground [&_svg]:size-3.5">
                  {contextIcon}
                  {contextLabel}
                </span>
              ) : null)}
            {context ?? contextLabel ? (
              <span aria-hidden className="text-muted-foreground/60">
                ›
              </span>
            ) : null}
            <span className="truncate text-muted-foreground">{heading}</span>
          </div>
          <div className="-mr-1.5 flex shrink-0 items-center gap-1">
            {expandHref ? (
              <Button
                asChild
                variant="ghost"
                size="icon"
                aria-label="Open full form"
                title="Open full form"
                className="text-muted-foreground hover:text-foreground"
              >
                <NextLink href={expandHref} onClick={onExpand}>
                  <PeekGlyphExpand />
                </NextLink>
              </Button>
            ) : null}
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close"
                title="Close"
                className="text-muted-foreground hover:text-foreground"
              >
                <PeekGlyphClose />
              </Button>
            </DialogClose>
          </div>
        </div>

        {/* Title + description — borderless, autosizing (the TitleField pattern). */}
        <div className="flex flex-col gap-0.5 px-5 pt-2">
          <Textarea
            autosize
            rows={1}
            autoFocus
            aria-label={heading}
            value={title}
            placeholder={titlePlaceholder}
            onChange={(e) => onTitleChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (canSubmit) onSubmit();
              }
            }}
            className={cn(
              BORDERLESS,
              "py-1 type-title font-medium leading-display tracking-tight text-foreground",
            )}
          />
          {onDescriptionChange ? (
            <Textarea
              autosize
              rows={1}
              aria-label="Description"
              value={description ?? ""}
              placeholder={descriptionPlaceholder}
              onChange={(e) => onDescriptionChange(e.target.value)}
              className={cn(BORDERLESS, "py-1 type-body leading-body text-foreground")}
            />
          ) : null}
        </div>

        {children ? <div className="px-5 pt-3">{children}</div> : null}

        {/* Property pills. */}
        {pills ? (
          <div className="flex flex-wrap items-center gap-2 px-5 pt-4">{pills}</div>
        ) : null}

        {error ? (
          <p className="px-5 pt-3 type-subtext text-danger">{error}</p>
        ) : null}

        {/* Footer — attachment left, Create-more + primary Create right. */}
        <div className="mt-2 flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            {onAttachmentsChange ? (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Attach files"
                  title="Attach files"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => fileRef.current?.click()}
                >
                  <Paperclip />
                </Button>
                {files.map((f, i) => (
                  <span
                    key={`${f.name}-${i}`}
                    className="inline-flex max-w-40 items-center gap-1 rounded-full bg-foreground/[0.07] px-2 py-0.5 type-subtext text-muted-foreground"
                  >
                    <span className="truncate">{f.name}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => removeFile(i)}
                      className="shrink-0 opacity-60 hover:opacity-100"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-3">
            {onCreateMoreChange ? (
              <label className="flex cursor-pointer items-center gap-2 type-body text-muted-foreground select-none">
                <Switch
                  size="sm"
                  checked={createMore ?? false}
                  onCheckedChange={onCreateMoreChange}
                />
                Create more
              </label>
            ) : null}
            <Button size="sm" onClick={onSubmit} disabled={!canSubmit || pending}>
              {pending ? "Creating…" : submitLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
